import { Cascader } from "antd";
import React from "react";
import { useQueryDependenciesGraphContext } from "../../context/questions-context";

export const QuestionDependencySelector: React.FC<{
    queryKey: string;
    value?: Record<string, string[]>;
    onChange?: (deps: Record<string, string[]>) => void;
    disabled?: boolean;
}> = ({ queryKey, value, onChange, disabled }) => {
    const { getDependableQueries, metaDictionary, queryAssetMeta } = useQueryDependenciesGraphContext();
    const searchAssetMeta = queryAssetMeta[queryKey];

    const dependableQueries = getDependableQueries(queryKey);

    const selectedOptionsToDependencies = (selectedOptions: any[]) => {
        return selectedOptions.reduce((opt1, opt2) => {
            const [queryKey, assetName] = [opt2[0].value, opt2[1].value];
            const { [queryKey]: currDeps, ...rest } = opt1;
            return {
                ...rest,
                [queryKey]: [...(currDeps || []), assetName],
            };
        }, {});
    };

    const dependenciesToSelectedOptions = (deps: Record<string, string[]>) => {
        return Object.entries(deps).flatMap(([predecessorKey, assetNames]) => {
            return assetNames.map((assetName) => [predecessorKey, assetName]);
        });
    };

    const getOptions = () => {
        if (!metaDictionary) return [];

        return (
            Object.entries(dependableQueries).map((d) => {
                const [_queryKey, queryDef] = d;
                const isQuestionGreaterThanCurrent = _queryKey >= queryKey;
                const selectableAssets = queryDef?.jsonQuery
                    ? queryDef?.jsonQuery.select
                          ?.map((s) => {
                              const _name = typeof s === "string" ? s : s.alias;
                              const metaAsset = searchAssetMeta?.[_name] || metaDictionary[_name];
                              if (!metaAsset) {
                                  return {
                                      label: _name,
                                      value: _name,
                                      disabled: true,
                                  };
                              }

                              return {
                                  label: metaAsset.title || metaAsset.name || _name,
                                  value: _name,
                                  disabled: metaAsset.asset_type === "metric",
                              };
                          })
                          .filter((s) => !s.disabled)
                    : [];
                const isEmptySelectableAssets = !selectableAssets || selectableAssets.length === 0;

                return {
                    label: queryDef.queryName,
                    value: _queryKey,
                    disabled: isQuestionGreaterThanCurrent || isEmptySelectableAssets,
                    children: selectableAssets,
                    disableCheckbox: true,
                };
            }) || []
        ).filter((s) => !s.disabled);
    };

    return (
        <Cascader
            className="flex justify-self-start w-full"
            options={getOptions()}
            onChange={(_, selectedOptions) => {
                onChange ? onChange(selectedOptionsToDependencies(selectedOptions)) : null;
            }}
            placeholder={"Belong to"}
            multiple
            value={dependenciesToSelectedOptions(value || {})}
            displayRender={(labels, selectedOptions = []) => {
                return <>{labels.join(" / ")}</>;
            }}
            disabled={disabled}
        />
    );
};
